import Vue from 'vue'
import lodash from 'lodash'
import i18n from '@/utils/i18n'
import store from '@/store'
import types from '@/store/mutation-types'
import * as tendersApi from '@/api/tenders/request'
import * as addendumsApi from '@/api/tender-addendums/request'
import * as bidApi from '@/api/bids/request'
import * as queryApi from '@/api/queries/request'
import { tenderStatus, addendumChangeType } from '@/utils/locale'
import { updateVueObject } from '@/store/util'
import navigation, { validTenderRoute, validTenderRouteExt } from '@/utils/navigation'
import { sortBids } from '@/utils/sortingHelper'
import { displayI18nFullName } from '@/utils/fullName'

const defaultState = getDefaultState()

function getDefaultState () {
  return {
    tenderDepartment: null,
    allTenders: {},
    lastSearchCount: '',
    allLastSearchedTenders: [],
    currentTender: resetCurrentTender(),
    currentBid: {},
    currentBidId: null,
    recentRevisionHistory: {},
    currentAddendumsHistory: [],
    lastSearchedAttributes: {},
    tncChecked: [],
    isOldTerm: false,
    maxTermVersion: 0,
    currentTermVersion: 0,
    addendumClassName: 'highlight-addendum-change', // default class,
  }
}
function resetCurrentTender () {
  return {
    general: {},
    bids: [],
    purchaseRequisition: null,
    service: [],
    accounts: [],
  }
}

function setTermsVersionAndTncChecked (state, currentTermsVersion, maxTermVersion) {
  state.currentTermVersion = currentTermsVersion

  if (currentTermsVersion === 1) {
    state.isOldTerm = true
  } else {
    state.isOldTerm = false
  }
  if (maxTermVersion) {
    state.maxTermVersion = maxTermVersion
  }
  setTncChecked(state)
}

function setTncChecked (state) {
  const path = getTncPath(state) + `[${state.currentTermVersion}]`
  const termsItems = i18n.te(path) ? i18n.t(path) : []
  state.tncChecked = termsItems.map(_ => {
    return { checked: false }
  })
}

function getTncPath (state) {
  const region = store.getters['regions/currentRegion'].name
  const departmentName = lodash.get(state.tenderDepartment, 'shortname', 'default').replace(/\W/g, '_')
  return `tender.${region}.${departmentName}.tnc`
}

function getMaxTermsVersion (rootGetters) {
  const region = rootGetters['regions/currentRegion'].name
  const path = `tender.${region}.maxVersion`
  const maxVersion = i18n.te(path) ? Number(i18n.t(path)) : 0
  return maxVersion
}

function getCocPath (state) {
  const region = store.getters['regions/currentRegion'].name
  const departmentName = lodash.get(state.tenderDepartment, 'shortname', 'default').replace(/\W/g, '_')
  return `tender.${region}.${departmentName}.conditionsOfContracts`
}

function setToValue (obj, value, path) {
  let i = 0
  for (; i < path.length - 1; i += 1) {
    if (!obj[path[i]]) {
      obj[path[i]] = {}; // eslint-disable-line
    }
    obj = obj[path[i]]; // eslint-disable-line
  }
  obj[path[i]] = value; // eslint-disable-line
}

export const getZipNames = (tender) => {
  const prNumber = deep(tender, 'general.prNumber')
  const prefix = prNumber ? `Tender_${prNumber}` : 'Tender'
  return { attachments: `${prefix}_Attachments.zip`,
    terms: `${prefix}_Terms.zip`}
}

/**
 * State Getters
 */
const getters = {
  tenderDepartment: state => state.tenderDepartment,
  allTenders: state => state.allTenders,
  lastSearchCount: state => state.lastSearchCount,
  allLastSearchedTenders: state => state.allLastSearchedTenders,
  currentTender: state => state.currentTender,
  accounts: state => state.currentTender.accounts || [],
  isAddendum: state => (state.currentTender.general || {}).version > 0,
  isTender: state => (state.currentTender.general || {}).tenderType === 'TENDER',
  currentTenderHistory: state => state.currentTender.histories,
  lastSearchedAttributes: state => state.lastSearchedAttributes,
  isBeforeIssue: state => (
    [tenderStatus.DRAFT, tenderStatus.WAPPR, tenderStatus.APPR, tenderStatus.REJECTED]
      .includes(deep(state, 'currentTender.general.status'))
  ),
  zipNames: state => {
    return getZipNames(state.currentTender)
  },
  tncChecked: state => state.tncChecked,
  currentBid: state => state.currentBid,
  currentBidId: state => state.currentBidId,
  isOldTerm: state => state.isOldTerm,
  maxTermVersion: state => state.maxTermVersion,
  currentTermVersion: state => state.currentTermVersion,
  isRepresentative: (state, getters, rootState, rootGetters) => {
    const currentUser = rootGetters['user/currentUser'] || {}
    return (state.currentTender.accounts || []).some(account =>
      account.id === currentUser.id && lodash.get(account, 'tenderAccount.invitedAt'))
  },
  isPicView: (state, getters, rootState, rootGetters) => {
    const currentUser = rootGetters['user/currentUser'] || {}
    return lodash.get(state.currentTender, 'general.managedBy') === currentUser.id
  },
  currentEditAccount: (state, getters, rootState, rootGetters) => {
    const currentUser = rootGetters['user/currentUser'] || {}
    let editAccount = {editorId: currentUser.id}
    if (getters.isRepresentative) {
      const currentAccount = state.currentTender.accounts.find((account) => {
        return account.tenderAccount.userId === currentUser.id
      })
      if (currentAccount && currentAccount.tenderAccount) {
        editAccount.editorName = displayI18nFullName(
          currentAccount.tenderAccount.contactFirstName,
          currentAccount.tenderAccount.contactLastName,
        )
      }
    } else {
      editAccount.editorName = currentUser.name
    }
    return editAccount
  },
  tncI18nPath: getTncPath,
  tncI18nTermsPath: state => getTncPath(state) + `[${state.currentTermVersion}]`,
  cocI18nPath: getCocPath,
}

/**
 * State Actions
 */
const actions = {

  async loadAllTenders ({ commit }) {
    const tenders = await tendersApi.getTenders()
    commit(types.TENDER_LOAD_ALL, { tenders })
  },
  /** View-all reads `[rows, count]` only in component state (cf. Project ViewAll `projectRows`). */
  async loadAllSearchedTenders (__, { query, diskCacheKey }) {
    return tendersApi.listAllTenders(query, diskCacheKey)
  },

  async loadTender ({ commit, dispatch }, tenderId) {
    if (!tenderId) {
      const err = {
        message: 'invalid param',
      }
      throw err
    }
    let tender = null
    try {
      tender = await tendersApi.getTender(tenderId)
      tender.bids = sortBids(tender.bids)
    } catch (error) {
      tender = defaultState.currentTender
      throw error
    } finally {
      commit(types.TENDER_UPDATE_CURRENT, { tenderId, tender })
      commit(types.TENDER_PREPARE_DATA_FOR_EDIT, { tender })
      const termsVersion = lodash.get(tender, 'general.termsVersion')
      dispatch('setTermsVersion', { termsVersion })
    }
  },

  validateRoute ({ state, commit, rootState, rootGetters }, { routeName } = {}) {
    const isEngineer = rootGetters['user/isEngineer']
    const data = state.currentTender.general
    const isValid = isEngineer ? validTenderRoute(data, routeName) : validTenderRouteExt(data, routeName)
    if (!isValid) {
      navigation.toHome()
      return false
    }
    return true
  },

  async refreshTender ({ state, dispatch }) {
    await dispatch('loadTender', state.currentTender.general.id)
  },

  /**
   * Update current tender to db, called within edit pages
   */
  async saveCurrentTender ({ state }, metaData = {}) {
    await tendersApi.updateTender(
      state.currentTender,
      state.currentTender.general.id,
      metaData.showNotification,
      metaData.updatedBidStatus,
    )
  },

  async updateTenderStatus ({ state, dispatch }, { id, status, historyDetail, action }) {
    await tendersApi.updateTenderStatus({ status, historyDetail }, id, action)
  },

  async saveAnalysisReco ({ state }) {
    await tendersApi.updateAnalysisReco(
      state.currentTender.general.analysis,
      state.currentTender.general.id,
    )
  },

  async loadAddendums ({ commit }, tenderId) {
    if (!tenderId) {
      return
    }
    let addendums = [];
    [addendums] =
      await addendumsApi.getAddendums(tenderId)
    commit(types.TENDER_LOAD_ADDENDUMS, { addendumsHistory: addendums })
  },

  async loadRecentRevision ({ commit, state }, { loadPendingAddendum = false, hasPendingAddendum = false } = {}) {
    const tenderGeneral = state.currentTender.general
    if (!tenderGeneral) {
      return
    }
    const { lastDiff, revisions } = await addendumsApi.getGeneralRevision(tenderGeneral.id)
    const secondLastRevision = revisions[1]
    const secondLastDiff = {}
    if (secondLastRevision) {
      secondLastRevision.revisions.forEach(
        (rec) => {
          setToValue(secondLastDiff, rec, rec.path)
        },
      )
    }
    const checkPendingAddendum = hasPendingAddendum || tendersApi.isTenderInAddendum(tenderGeneral, true)
    let generalRevision = {}
    if (checkPendingAddendum && !loadPendingAddendum) {
      generalRevision = { ...secondLastDiff, documents: [] }
      generalRevision.documents = tenderGeneral.version >= 2
        ? await addendumsApi.getRevisionDocuments(tenderGeneral.id, tenderGeneral.version - 2)
        : null
    } else {
      generalRevision = { ...lastDiff, documents: [] }
      generalRevision.documents = tenderGeneral.version >= 1
        ? await addendumsApi.getRevisionDocuments(tenderGeneral.id, tenderGeneral.version - 1)
        : null
    }
    commit(types.TENDER_LOAD_RECENT_REVISION, { revisionHistory: generalRevision })
  },

  async loadDeclinedRevision ({ commit, state }) {
    const tenderGeneral = state.currentTender.general
    if (!tenderGeneral) {
      return
    }
    const { revisions } = await addendumsApi.getGeneralRevision(tenderGeneral.id)
    const declineRevision = revisions.find(revision =>
      revision.version === tenderGeneral.version,
    )
    const diff = {}
    if (declineRevision) {
      declineRevision.revisions.forEach(
        (rec) => {
          setToValue(diff, rec, rec.path)
        },
      )
    }
    let generalRevision = { ...diff, documents: [] }
    generalRevision.documents = tenderGeneral.version >= 1
      ? await addendumsApi.getRevisionDocuments(tenderGeneral.id, tenderGeneral.version - 1)
      : null
    commit(types.TENDER_LOAD_RECENT_REVISION, { revisionHistory: generalRevision })
  },

  async createRevision ({ state }) {
    if (state.currentTender.general.status === tenderStatus.ISSUED) {
      await addendumsApi.createRevision(state.currentTender.general.id)
    }
  },
  setLastSearchedAttributes ({ commit }, lastSearchedAttributes) {
    const attributes = lodash.cloneDeep(lastSearchedAttributes)
    commit(types.SET_LAST_SEARCH_ATTRIBUTES, { lastSearchedAttributes: attributes })
  },

  /**
   * Tender Bids & Queries
   */

  async submitBid ({ state, commit }, { bidId, request }) {
    await bidApi.updateBid(request, bidId)
  },

  async submitBidStatus ({ state, commit }, { bidId, request, action }) {
    await bidApi.updateBidStatus(bidId, request, action)
  },

  async refreshBid ({ state, commit }, { id, bid }) {
    let updatedBid = bid
    if (!updatedBid && id) {
      updatedBid = await bidApi.getBidById(id)
    }
    if (updatedBid) {
      commit(types.TENDER_SET_CURRENT_BID, { bid: updatedBid.bid })
      commit(types.TENDER_REFRESH_BID, { bid: updatedBid.bid })
    }
  },

  async refreshCurrentBid ({ state, dispatch, commit }, bidId) {
    if (bidId && state.currentBidId !== bidId) {
      commit(types.TENDER_CURRENT_BID_ID, { id: bidId })
    }
    if (!state.currentBidId) return
    const bid = await bidApi.getBidById(state.currentBidId)
    commit(types.TENDER_SET_CURRENT_BID, { bid })
    await dispatch('refreshBid', { id: state.currentBidId, bid })
  },

  async createQuery ({ state, dispatch }) {
    await queryApi.createQuery(state.currentBidId)
    await dispatch('refreshBid', { id: state.currentBidId })
  },

  async saveQuery ({ state }, { query, showNotification = true }) {
    if (!query) return
    await queryApi.updateQuery(query.id, query, showNotification)
  },

  async updateQueryStatus ({ state }, { query, status, showNotification = true }) {
    await queryApi.updateQueryStatus(query.id, status)
  },

  async deleteQuery ({ state }, queryId) {
    await queryApi.removeQuery(queryId)
  },

  /**
   * Tender Award multi PO/Contracts
   */
  async bindNumber ({ state }, { tenderId, resourceMap, type }) {
    await tendersApi.bindNumber(tenderId, resourceMap, type)
  },

  async updateAddendumComment ({ state }, { tenderId, commentIndex, content }) {
    const res = await addendumsApi.updateAddendumComment(tenderId, commentIndex, content)
    return res
  },
  setTermsVersion ({ state, commit, rootState, rootGetters }, { termsVersion }) {
    const maxTermVersion = getMaxTermsVersion(rootGetters)
    const version = !termsVersion ? maxTermVersion : termsVersion
    commit(types.TENDER_SET_TERM_VERSION, { currentTermsVersion: version, maxTermVersion })
  },
}

/**
 * State Mutations
 */
const mutations = {
  /**
   * Maintain all tenders list
   */
  [types.TENDER_LOAD_ALL] (state, { tenders }) {
    state.allTenders = {}
    state.currentTender = resetCurrentTender()
    if (tenders) {
      tenders.reduce((allTenders, tender) => {
        allTenders[tender.general.id] = tender
        return allTenders
      }, state.allTenders)
    }
  },
  [types.TENDER_UPDATE_DEPARTMENT] (state, { department }) {
    state.tenderDepartment = department
    setTncChecked(state)
  },
  [types.TENDER_LOAD_ALL_LAST_SEARCHED] (state, { allLastSearchedTenders, lastSearchCount }) {
    state.allLastSearchedTenders = allLastSearchedTenders
    state.lastSearchCount = lastSearchCount
  },
  [types.TENDER_UPDATE_CURRENT] (state, { tenderId, tender }) {
    Vue.set(state.allTenders,
      tenderId,
      { ...state.allTenders[tenderId], ...tender })
  },

  /**
   * Current tender record mutations
   */
  [types.TENDER_PREPARE_DATA_FOR_EDIT] (state, { tender }) {
    state.currentTender = tender
  },
  [types.TENDER_UPDATE_FIELD] (state, { sectionName, fieldName, newValue }) {
    Vue.set(state.currentTender[sectionName], fieldName, newValue)
  },
  [types.TENDER_UPDATE_PATH_FIELD] (state, { path, newValue, remove = false }) {
    updateVueObject(state.currentTender, path, newValue, remove)
  },
  [types.TENDER_UPDATE_EDIT_STATUS_FIELDS] (state, {editorName, editorId, editAt}) {
    Object.assign(state.currentTender.general, {editorName, editorId, editAt})
  },
  [types.ADDENDUM_UPDATE_CLASS_NAME] (state, { addendumClassName }) {
    Vue.set(state.addendumClassName, addendumClassName); // eslint-disable-line
  },
  [types.TENDER_SET_TERM_VERSION] (state, { currentTermsVersion, maxTermVersion }) {
    setTermsVersionAndTncChecked(state, currentTermsVersion, maxTermVersion)
  },

  /**
   * Current revision history record mutations
   */
  [types.TENDER_LOAD_RECENT_REVISION] (state, { revisionHistory }) {
    state.recentRevisionHistory = revisionHistory; // eslint-disable-line
  },
  [types.TENDER_LOAD_ADDENDUMS] (state, { addendumsHistory }) {
    state.currentAddendumsHistory = addendumsHistory; // eslint-disable-line
  },
  [types.TENDER_CLEAR_SECTION_FOR_REFERENCE] (state, isAddendum = false) {
    const service = state.currentTender.service
    if (service && service.length > 1 && service[service.length - 1].forReference) {
      const sectionForRefer = service[service.length - 1]
      const allPendingAdded = sectionForRefer.lineItems.every(item => item.addendumLabel === addendumChangeType.pending.added)
      if ((!isAddendum || allPendingAdded) && !sectionForRefer.checked) {
        service.pop()
      }
    }
  },

  /**
   * Tender Bids & Queries
   */
  [types.TENDER_REFRESH_BID] (state, { bid = {} }) {
    state.currentTender.bids.forEach(rec => {
      if (rec.id === bid.id) {
        rec.queries = JSON.parse(JSON.stringify(bid.queries))
      }
    })
  },

  [types.TENDER_CURRENT_BID_ID] (state, { id }) {
    state.currentBidId = id
  },

  [types.TENDER_SET_CURRENT_BID] (state, { bid }) {
    state.currentBid = bid
    if (deep(bid, 'id') !== state.currentBidId) {
      state.currentBidId = bid.id
    }
  },

  [types.TENDER_UPDATE_QUERY_BY_PATH] (state, { path, newValue }) {
    const queryPath = ['queries', ...path]
    updateVueObject(state.currentBid, queryPath, newValue)
  },

  /**
   * Search all tenders
   */
  [types.SET_LAST_SEARCH_ATTRIBUTES] (state, {lastSearchedAttributes}) {
    state.lastSearchedAttributes = lastSearchedAttributes
  },
  [types.RESET_STATE] (state) {
    const stateVal = getDefaultState()
    for (const field in stateVal) {
      Vue.set(state, field, stateVal[field])
    }
  },
  [types.UPDATE_TNC] (state, { index = 0, checked = false }) {
    Vue.set(state.tncChecked[index], 'checked', checked)
  },
  [types.RESET_TNC] (state) {
    setTncChecked(state)
  },
  [types.SET_TNC] (state, { tncChecked }) {
    Vue.set(state, 'tncChecked', tncChecked)
  },
}

export default {
  namespaced: true,
  state: defaultState,
  getters,
  actions,
  mutations,
}
